<script setup>
import {ref, computed, nextTick, onMounted, watch} from 'vue';
import {useAssistantMarkdown} from '~/composables/useAssistantMarkdown';
import {useAssistant} from '~/composables/useAssistant';
import {useAuthService} from '~/composables/useAuthService';

// Assistant replies contain markdown (the prompt asks for **bold** labels and
// numbered steps). Raw HTML stays escaped (markdown-it default). User messages
// stay plain text.
//
// Links come in two kinds. Help-doc product links are RELATIVE paths
// ("/admin", "/clubs") — environment-agnostic by design — and navigate
// same-tab through the router, so the docked panel stays open over the page
// it just sent the user to. External links open in a new tab.
// Doc links may carry a {clubId} placeholder ("/admin/clubs/{clubId}/members")
// so docs can point at club-scoped pages without knowing anyone's ids. It is
// filled from the club in context: the club being viewed, else the user's club
// when they administer exactly one. With no club (or a choice to make between
// several) the link is UNRESOLVABLE and is rendered as plain text — a dead link
// is worse than no link.
const route = useRoute();
const {user} = useAuthService();
const clubIdInContext = computed(() => {
  const fromRoute = route.params?.clubId;
  if (fromRoute) return String(fromRoute);
  const adminFor = user.value?.adminFor;
  return adminFor?.length === 1 ? String(adminFor[0]) : null;
});

// Rendering lives in useAssistantMarkdown so the live chat and the super-admin
// transcript view cannot drift apart about what a reply looks like.
const {renderMarkdown} = useAssistantMarkdown(() => clubIdInContext.value);

// v-html anchors bypass vue-router, so intercept internal-link clicks and
// route them (a full page load would drop the panel and re-bootstrap the app).
const router = useRouter();
const onMarkdownClick = (event) => {
  const anchor = event.target?.closest?.('a');
  if (!anchor) return;
  const href = anchor.getAttribute('href') || '';
  if (!href.startsWith('/')) return;
  event.preventDefault();
  router.push(href);
};

const props = defineProps({
  // Dialog context: the overlay provides its own title bar, and the chat can
  // use more vertical space.
  hideHeader: {type: Boolean, default: false},
});

const {
  messages,
  streaming,
  limitNotice,
  notice,
  retryNotice,
  sendMessage,
  rateMessage,
  newConversation,
} = useAssistant();

const draft = ref('');
const scroller = ref(null);
const inputRef = ref(null);
const maxChars = 1000;

const submit = async () => {
  // Field stays enabled while a reply streams (so it never loses focus); the
  // send is gated instead. Guard here so Enter mid-stream doesn't clear the box.
  // Also gated on the notice: no disclosure on screen, no sending.
  if (streaming.value || !notice.value) return;
  const text = draft.value.trim();
  if (!text) return;
  draft.value = '';
  inputRef.value?.focus();
  await sendMessage(text);
};

const scrollToBottom = async () => {
  await nextTick();
  if (scroller.value) {
    scroller.value.scrollTop = scroller.value.scrollHeight;
  }
};

// Keep the newest message in view while streaming, and land at the bottom when
// the popup is (re)opened with an existing conversation.
watch(messages, scrollToBottom, {deep: false});
onMounted(scrollToBottom);
</script>

<template>
  <div class="tl-card assistant-card">
    <div v-if="!hideHeader || messages.length > 0" class="assistant-header">
      <div v-if="!hideHeader" class="assistant-title">
        <v-icon size="22" color="primary">mdi-robot-happy-outline</v-icon>
        <span>Tap Leagues assistant</span>
        <v-chip size="x-small" color="primary" variant="tonal" class="ml-1">Beta</v-chip>
      </div>
      <v-spacer v-else/>
      <v-btn
          v-if="messages.length > 0"
          size="small"
          variant="text"
          prepend-icon="mdi-autorenew"
          @click="newConversation"
      >
        New chat
      </v-btn>
    </div>

    <div ref="scroller" class="assistant-messages" :class="{'assistant-messages--tall': hideHeader}">
      <!-- Monitoring / data-use notice. Always the first thing in the thread (it
           stays at the top as the conversation grows), so consent is visible
           before the first message is sent. -->
      <div class="assistant-notice">
        <p v-if="notice">{{ notice }}</p>
        <p v-else>
          Couldn't load the chat right now.
          <a href="#" @click.prevent="retryNotice">Try again</a>
        </p>
      </div>

      <div v-if="messages.length === 0" class="assistant-empty">
        <p>Ask me anything about using Tap Leagues: joining clubs, ladders, challenges, entering results…</p>
        <p class="assistant-beta-note">
          <strong>In beta.</strong> It can help with using Tap Leagues, and it can make mistakes.
        </p>
      </div>

      <div
          v-for="(message, index) in messages"
          :key="message.id || index"
          class="assistant-row"
          :class="message.role"
      >
        <div class="assistant-bubble" :class="message.role">
          <span v-if="message.role === 'assistant' && message.pending && !message.text" class="assistant-typing">
            <v-progress-circular indeterminate size="14" width="2"/>
            Thinking…
          </span>
          <!-- A failure with nothing streamed: say why, in the bubble. Rendering
               empty markdown leaves a bubble with nothing in it. -->
          <div v-else-if="message.role === 'assistant' && !message.text && message.error"
               class="assistant-empty">{{ message.error }}</div>
          <!-- eslint-disable-next-line vue/no-v-html — markdown-it escapes raw HTML -->
          <div v-else-if="message.role === 'assistant'" class="assistant-md" @click="onMarkdownClick" v-html="renderMarkdown(message.text)"/>
          <span v-else class="assistant-text">{{ message.text }}</span>

          <div v-if="message.error && message.text" class="assistant-error">{{ message.error }}</div>

          <div
              v-if="message.role === 'assistant' && message.id && !message.pending && !message.error"
              class="assistant-actions"
          >
            <v-btn
                icon
                size="x-small"
                variant="text"
                :color="message.rating === 1 ? 'primary' : undefined"
                @click="rateMessage(message, 1)"
            >
              <v-icon size="16">mdi-thumb-up-outline</v-icon>
            </v-btn>
            <v-btn
                icon
                size="x-small"
                variant="text"
                :color="message.rating === -1 ? 'primary' : undefined"
                @click="rateMessage(message, -1)"
            >
              <v-icon size="16">mdi-thumb-down-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Any 429 lands here, including codes this build has never heard of: the
         server owns the wording, so a new limit explains itself without a release.
         "Start a new chat" is ONLY for conversationFull — for a daily limit or the
         org-wide spend cap it fixes nothing and reads as blaming the user. -->
    <v-alert
        v-if="limitNotice"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-2"
    >
      {{ limitNotice.message }}
      <template v-if="limitNotice.code === 'conversationFull'">
        <v-btn size="small" variant="text" @click="newConversation">Start a new chat</v-btn>
      </template>
    </v-alert>

    <v-form @submit.prevent="submit">
      <v-textarea
          ref="inputRef"
          v-model="draft"
          rows="1"
          auto-grow
          max-rows="4"
          :counter="maxChars"
          :maxlength="maxChars"
          variant="outlined"
          density="compact"
          hide-details="auto"
          placeholder="Ask a question about Tap Leagues…"
          @keydown.enter.exact.prevent="submit"
      >
        <template #append-inner>
          <v-btn
              icon
              size="small"
              color="primary"
              variant="text"
              :disabled="streaming || !draft.trim() || !notice"
              @click="submit"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </template>
      </v-textarea>
    </v-form>

    <p class="assistant-disclaimer">
      AI assistant (beta). It can make mistakes. For anything it can't help with,
      <NuxtLink :to="{ name: 'Contact' }">contact us</NuxtLink>.
    </p>
  </div>
</template>

<style scoped lang="scss">
.assistant-card {
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.assistant-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 15px;
}

.assistant-messages {
  overflow-y: auto;
  max-height: 420px;
  min-height: 160px;

  &--tall {
    max-height: none;
    flex: 1;
  }
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 2px 12px;
}

.assistant-notice {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  padding: 10px 12px;
  color: rgb(var(--v-theme-charcoal));
  font-size: 11.5px;
  line-height: 1.55;

  p {
    margin: 0;
    // The notice is served as plain text and rendered verbatim — the client
    // doesn't parse its structure, so preserve the author's line breaks.
    white-space: pre-line;
  }

  a {
    color: rgb(var(--v-theme-primary));
  }
}

.assistant-empty {
  color: rgb(var(--v-theme-charcoal));
  font-size: 13.5px;
  line-height: 1.6;
  padding: 24px 8px;
  text-align: center;
}

.assistant-beta-note {
  margin: 14px auto 0;
  max-width: 420px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.06);
  font-size: 12.5px;

  strong {
    color: rgb(var(--v-theme-primary));
  }
}

.assistant-row {
  display: flex;

  &.user {
    justify-content: flex-end;
  }
}

.assistant-bubble {
  max-width: 85%;
  border-radius: 14px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.6;

  &.user {
    background: rgb(var(--v-theme-brandTint));
  }

  &.assistant {
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.assistant-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-md {
  word-break: break-word;

  :deep(p) {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ol),
  :deep(ul) {
    margin: 0 0 8px;
    padding-left: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(li) {
    margin-bottom: 2px;
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(code) {
    background: rgba(var(--v-theme-borderLight), 0.5);
    border-radius: 4px;
    padding: 1px 5px;
    font-size: 12.5px;
  }

  :deep(a) {
    color: rgb(var(--v-theme-primary));
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4) {
    font-size: 14px;
    font-weight: 700;
    margin: 0 0 6px;
  }
}

.assistant-typing {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgb(var(--v-theme-charcoal));
  font-size: 13px;
}

.assistant-empty {
  font-size: 0.85rem;
  opacity: 0.75;
}

.assistant-error {
  color: rgb(var(--v-theme-error));
  font-size: 12.5px;
  margin-top: 6px;
}

.assistant-actions {
  display: flex;
  gap: 2px;
  margin-top: 4px;
}

.assistant-disclaimer {
  font-size: 11.5px;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 8px;

  a {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
